import { apiClient } from "../api/apiClient.js";

// P146: thin fetch wrappers over the Detailed Worker Profile enrichment API
// (GET/PUT /api/backoffice/worker-applications/{id}/detailed-profile and its
// /document upload).
//
// Visibility and write permission are always resolved on the backend from the
// caller's own session - nothing in this file sends an agent/organization id
// to claim scope, and the `canEdit` flag the read returns is the backend's
// own answer, not something the UI decides.

const BASE_PATH = "/backoffice/worker-applications";

function profilePath(applicationId) {
  if (!applicationId) {
    throw new Error("workerDetailedProfileApi requires an applicationId");
  }
  return `${BASE_PATH}/${encodeURIComponent(applicationId)}/detailed-profile`;
}

export function getWorkerDetailedProfile(applicationId) {
  return apiClient.get(profilePath(applicationId));
}

// Exactly the fields the P146 UpdateWorkerDetailedProfileRequest accepts.
// Anything else present on the form state is dropped rather than forwarded,
// so a stray UI-only key (section tab, dirty flag, ...) can never reach the
// backend. Order matches the editor's own section order for readability.
const SAVE_FIELD_KEYS = [
  "fullNameNative",
  "fullNameEnglish",
  "localName",
  "genderCode",
  "dateOfBirth",
  "nationality",
  "phoneNumber",
  "whatsAppNumber",
  "preferredLanguageCode",
  "homeCountryAddress",
  "passportNumber",
  "passportExpiryDate",
  "passportIssuingCountry",
  "primarySkillCode",
  "primarySkillOtherText",
  "yearsOfExperience",
  "secondarySkills",
  "spokenLanguages",
  "certifications",
  "desiredJobTypes",
  "expectedSalaryAmount",
  "expectedSalaryCurrency",
  "expectedSalaryPeriod",
  "preferredProvinces",
  "availableFromDate",
  "backofficeNotes",
  // --- P147 flat fields. A field missing from this list is silently dropped
  // rather than sent, so every new column has to be added here too. ---
  "titlePrefix",
  "firstName",
  "lastName",
  "firstNameEn",
  "lastNameEn",
  "citizenNo",
  "email",
  "currentAddress",
  "currentProvince",
  "emergencyContactName",
  "emergencyContactPhone",
  "emergencyContactRelation",
  "bloodType",
  "medicalCondition",
  "educationLevelSummary",
];

/**
 * P147: the three health answers are tri-state - true / false / not asked.
 * They bypass the string normalization below entirely, because trimming a
 * boolean turns `false` into a filled-looking value and `null` ("not asked")
 * into a stored "no".
 */
const BOOLEAN_FIELD_KEYS = ["smokingFlag", "drinkingFlag", "covidVaccinationFlag"];

/**
 * P147: the five repeating sections. Sent as arrays of plain objects, each
 * restricted to its own known keys so no UI-only field (a row id, a focus
 * flag) can leak into the request. An array that is absent from the form state
 * is omitted entirely, which the backend reads as "leave this section alone" -
 * an empty array, by contrast, really does clear it.
 */
const SECTION_FIELD_KEYS = {
  languages: ["languageName", "listeningLevel", "speakingLevel", "readingLevel", "writingLevel"],
  skillItems: ["skillName", "experienceYears"],
  educations: ["institutionName", "educationLevelText", "major", "startYear", "endYear", "gpaText"],
  experiences: ["companyName", "jobTitle", "countryName", "startYear", "endYear", "salaryText", "remark"],
  visas: ["visaType", "issuingCountry", "visaNumber", "issueDate", "expiryDate"],
};

/** Per-section keys the backend types as int? - blank must become null, never 0. */
const SECTION_NUMERIC_KEYS = new Set(["experienceYears", "startYear", "endYear"]);

function normalizeSectionValue(key, raw) {
  if (SECTION_NUMERIC_KEYS.has(key)) {
    const trimmed = typeof raw === "string" ? raw.trim() : raw;
    if (trimmed === "" || trimmed === null || trimmed === undefined) {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed === "" ? null : trimmed;
  }

  return raw === undefined ? null : raw;
}

// The two numeric fields the backend types as int? - an empty form control
// must become null (cleared), never 0 (a real "no experience"/"free" claim)
// and never "" (which the backend would reject as a malformed number).
const NUMERIC_FIELD_KEYS = new Set(["yearsOfExperience", "expectedSalaryAmount"]);

/**
 * Normalizes the editor's form state into the exact request body shape:
 * whitespace-only text becomes null (an empty field, not a stored blank),
 * blank numbers become null, and unknown keys are dropped.
 */
export function buildSaveBody(formState = {}) {
  const body = {};
  for (const key of SAVE_FIELD_KEYS) {
    const raw = formState[key];
    if (NUMERIC_FIELD_KEYS.has(key)) {
      const trimmed = typeof raw === "string" ? raw.trim() : raw;
      if (trimmed === "" || trimmed === null || trimmed === undefined) {
        body[key] = null;
      } else {
        const parsed = Number(trimmed);
        body[key] = Number.isFinite(parsed) ? Math.trunc(parsed) : null;
      }
      continue;
    }

    if (typeof raw === "string") {
      const trimmed = raw.trim();
      body[key] = trimmed === "" ? null : trimmed;
      continue;
    }

    body[key] = raw === undefined ? null : raw;
  }

  for (const key of BOOLEAN_FIELD_KEYS) {
    const raw = formState[key];
    body[key] = raw === true || raw === false ? raw : null;
  }

  for (const [section, keys] of Object.entries(SECTION_FIELD_KEYS)) {
    const rows = formState[section];
    if (!Array.isArray(rows)) {
      continue;
    }
    body[section] = rows.map((row) => {
      const shaped = {};
      for (const key of keys) {
        shaped[key] = normalizeSectionValue(key, row?.[key]);
      }
      return shaped;
    });
  }
  return body;
}

export function saveWorkerDetailedProfile(applicationId, formState) {
  return apiClient.put(profilePath(applicationId), buildSaveBody(formState));
}

/**
 * Uploads a real passport/identity document file (from the file picker or a
 * camera capture). The backend validates the content type and size with the
 * same storage implementation the public quick-register flow uses, and the
 * response is the full refreshed profile - so a caller never has to guess
 * what the document metadata became.
 */
export function uploadWorkerPassportDocument(applicationId, file) {
  if (!file) {
    throw new Error("uploadWorkerPassportDocument requires a file");
  }
  const formData = new FormData();
  formData.append("passportDocument", file, file.name || "passport.jpg");
  return apiClient.postForm(`${profilePath(applicationId)}/document`, formData);
}

// --- P157-R9: the worker's own photo, and extra documents beyond the single
// passport slot. All three writes return the full refreshed profile, exactly
// like the passport upload above, so a caller applies the server's own answer
// instead of patching its local copy and hoping the two agree.

export function uploadWorkerPhoto(applicationId, file) {
  if (!file) {
    throw new Error("uploadWorkerPhoto requires a file");
  }
  const formData = new FormData();
  formData.append("photo", file, file.name || "photo.jpg");
  return apiClient.postForm(`${profilePath(applicationId)}/photo`, formData);
}

/**
 * `documentType` is the operator's own free-text label ("ใบรับรองแพทย์"), never
 * a closed enum - the documents a migrant worker carries vary by country and by
 * employer. Empty is allowed; the backend stores NULL.
 */
export function uploadWorkerExtraDocument(applicationId, file, documentType) {
  if (!file) {
    throw new Error("uploadWorkerExtraDocument requires a file");
  }
  const formData = new FormData();
  formData.append("document", file, file.name || "document.jpg");
  if (documentType) {
    formData.append("documentType", documentType);
  }
  return apiClient.postForm(`${profilePath(applicationId)}/documents`, formData);
}

// --- P158: work-sample VIDEOS. A construction worker is judged on footage of
// the work, so this is the portfolio an employer actually watches.

/**
 * `durationSeconds` is read by the browser off the loaded file before upload.
 * Pass null when it could not be read - the backend stores NULL rather than a
 * guess, because "0:30" shown on a three-minute clip is worse than no length.
 */
export function uploadWorkerWorkSample(applicationId, file, { title, durationSeconds } = {}) {
  if (!file) {
    throw new Error("uploadWorkerWorkSample requires a file");
  }
  const formData = new FormData();
  formData.append("video", file, file.name || "work-sample.mp4");
  if (title) {
    formData.append("title", title);
  }
  if (Number.isFinite(durationSeconds) && durationSeconds > 0) {
    formData.append("durationSeconds", String(Math.round(durationSeconds)));
  }
  return apiClient.postForm(`${profilePath(applicationId)}/work-samples`, formData);
}

export function deleteWorkerWorkSample(applicationId, workSampleId) {
  if (!workSampleId) {
    throw new Error("deleteWorkerWorkSample requires a workSampleId");
  }
  return apiClient.delete(`${profilePath(applicationId)}/work-samples/${encodeURIComponent(workSampleId)}`);
}

/**
 * The authenticated bytes of one work sample, as a blob for playback.
 *
 * Honest note on what this does and does not do: the BACKEND serves this route
 * with byte-range processing, so a player that could talk to it directly would
 * stream and seek without downloading the whole clip. A <video> element cannot,
 * because it cannot send an Authorization header, and this API takes no token
 * any other way. So the frontend fetches the file once, authenticated, and plays
 * it from a blob URL: seeking works normally afterwards, but the first frame
 * waits for the whole download. Giving the route a signed URL or a cookie is
 * what would unlock real streaming; the server side is already ready for it.
 */
export function getWorkerWorkSampleBlob(applicationId, workSampleId) {
  return apiClient.getBlob(`${profilePath(applicationId)}/work-samples/${encodeURIComponent(workSampleId)}/file`);
}

/** Soft delete on the backend - the row and its bytes stay recoverable. */
export function deleteWorkerExtraDocument(applicationId, documentId) {
  if (!documentId) {
    throw new Error("deleteWorkerExtraDocument requires a documentId");
  }
  return apiClient.delete(`${profilePath(applicationId)}/documents/${encodeURIComponent(documentId)}`);
}

/**
 * P147: resolves a Worker Profile row (dbo.WmxWorkers) to the Quick Register
 * application its detailed record lives on, so the Worker Profiles screen and
 * the Applications screen edit ONE record instead of two copies that drift.
 *
 * The response also carries the honest negative case: a worker who never came
 * through Quick Register has `hasDetailedProfile: false` and a Thai
 * `unavailableReason`, which the UI shows instead of an empty form that could
 * never be saved.
 */
export function getWorkerProfileDetailLink(workerId) {
  if (!workerId) {
    throw new Error("getWorkerProfileDetailLink requires a workerId");
  }
  return apiClient.get(`/backoffice/worker-profiles/${encodeURIComponent(workerId)}/detailed-profile-link`);
}
